import { useState } from 'react';

import { ReparoApi } from '../api';
import type { RateBasis } from '../api/inventory';

import useToaster from './toaster/useToaster';

/**
 * Explore-then-commit state for the DoH/DRR rate policy behind `RateBasisControl`.
 *
 * The draft basis/window drive the request only; they are session-only and reset on reload.
 * Saving commits them to the organisation, which re-bases every downstream consumer of DoH
 * (alerts, scheduled reports, replenishment, planning) — hence the warning copy on the control.
 *
 * `setSaved` is for the container to feed the org defaults back in once the API response that
 * echoes them lands, so "dirty" is measured against the real saved value rather than the
 * placeholder this starts with.
 */
export interface RatePolicyState {
    savedBasis: RateBasis;
    savedWindow: number;
    effectiveBasis: RateBasis;
    effectiveWindow: number;
    isDirty: boolean;
    saving: boolean;
    /** Feed the org's saved defaults in from an API response. No-op once the user has a draft. */
    setSaved: (basis: RateBasis, window: number) => void;
    setBasis: (basis: RateBasis) => void;
    setWindow: (window: number) => void;
    reset: () => void;
    save: () => Promise<void>;
}

export function useRatePolicy(merchantId: string | null | undefined): RatePolicyState {
    const { showToastSuccess, showToastWarning } = useToaster();

    const [savedBasis, setSavedBasis] = useState<RateBasis>('BACKWARD');
    const [savedWindow, setSavedWindow] = useState(30);
    const [draftBasis, setDraftBasis] = useState<RateBasis | null>(null);
    const [draftWindow, setDraftWindow] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);

    const effectiveBasis = draftBasis ?? savedBasis;
    const effectiveWindow = draftWindow ?? savedWindow;
    // The window is a length and the basis only names its direction, so a window change is a
    // real unsaved preview on Forecast exactly as it is on Past Sales.
    const isDirty = effectiveBasis !== savedBasis || effectiveWindow !== savedWindow;

    const reset = () => {
        setDraftBasis(null);
        setDraftWindow(null);
    };

    const save = async () => {
        if (!merchantId || merchantId === 'default') return;
        setSaving(true);
        try {
            await ReparoApi({
                method: 'PATCH',
                url: `/organizations/${merchantId}`,
                body: JSON.stringify({
                    lookup: effectiveBasis,
                    salesLookbackDays: effectiveWindow,
                }),
            });
            setSavedBasis(effectiveBasis);
            setSavedWindow(effectiveWindow);
            reset();
            showToastSuccess({
                title: 'Saved as default',
                subtitle: 'Alerts, reports and planning will now use this basis.',
            });
        } catch {
            showToastWarning({ title: 'Could not save the default. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    return {
        savedBasis,
        savedWindow,
        effectiveBasis,
        effectiveWindow,
        isDirty,
        saving,
        setSaved: (basis, window) => {
            setSavedBasis(basis);
            setSavedWindow(window);
        },
        setBasis: setDraftBasis,
        setWindow: setDraftWindow,
        reset,
        save,
    };
}
